// Bell + popover notification center (N1) — All / Mentions / Activity tabs.
import { useEffect, useRef, useState } from "react";
import {
  Badge, Box, IconButton, Menu, MenuItem, Tab, Tabs, Typography,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { brainTokens } from "../design/brainTheme";
import { Icon } from "../design/ui";
import { useNotificationsContext } from "../lib/NotificationsProvider";
import { useCorrespondence } from "../correspondence/CorrespondenceProvider";
import type { BrainNotification } from "../lib/brain-api";
import { relDate } from "./format";

const c = brainTokens.color;

const pulseAnim = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.18); }
  100% { transform: scale(1); }
`;

function reasonLabel(n: BrainNotification): string {
  switch (n.reason) {
    case "mentioned": return "because you were mentioned";
    case "reply": return "because someone replied to you";
    case "owner": return "because you own this record";
    case "lead": return "because you lead this record";
    case "assignee": return "because you are the assignee";
    default: return n.reason;
  }
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { openCorrespondence } = useCorrespondence();
  const {
    count, items, tab, loading, pulse, inboxOpenNonce, openInbox, changeTab, markDone,
  } = useNotificationsContext();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const lastNonce = useRef(0);
  const open = Boolean(anchor);

  // Today "Open inbox →" bumps inboxOpenNonce — open the popover against the bell button.
  useEffect(() => {
    if (!inboxOpenNonce || inboxOpenNonce === lastNonce.current) return;
    lastNonce.current = inboxOpenNonce;
    if (btnRef.current) setAnchor(btnRef.current);
  }, [inboxOpenNonce]);

  const onOpen = async (el: HTMLElement) => {
    setAnchor(el);
    await openInbox();
  };

  const onItem = async (n: BrainNotification) => {
    await markDone(n.eventId);
    setAnchor(null);
    if (n.url) {
      // Same gesture as a Desk row: land on the record with the dock's
      // Correspondence tab open, so the thread is right there.
      openCorrespondence();
      navigate(n.url);
    }
  };

  return (
    <>
      <IconButton
        ref={btnRef}
        aria-label="Notifications"
        title="Notifications"
        onClick={(e) => void onOpen(e.currentTarget)}
        sx={{
          color: c.topBarText,
          animation: pulse ? `${pulseAnim} 0.6s ease 0s 2` : undefined,
        }}
      >
        <Badge
          badgeContent={count.unseen > 0 ? count.unseen : 0}
          color="error"
          overlap="circular"
          sx={{ "& .MuiBadge-badge": { fontSize: 10, minWidth: 16, height: 16 } }}
        >
          <Icon name="bell" size={23} color={c.topBarText} />
        </Badge>
      </IconButton>
      <Menu
        open={open}
        onClose={() => setAnchor(null)}
        anchorEl={anchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxWidth: "92vw", mt: 1 } }}
        // Kill MenuList's default 8px vertical padding — it reads as a stray
        // white strip above "Inbox" and below the footer button.
        MenuListProps={{ sx: { py: 0 } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: c.ink }}>Inbox</Typography>
        </Box>
        <Tabs
          value={tab}
          onChange={(_, v) => void changeTab(v)}
          variant="fullWidth"
          sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, fontSize: 12, textTransform: "none" } }}
        >
          <Tab value="all" label="All" />
          <Tab value="mentions" label="Mentions" />
          <Tab value="activity" label="Activity" />
        </Tabs>
        <Box sx={{ maxHeight: 360, overflowY: "auto", py: 0.5 }}>
          {loading && items.length === 0 ? (
            <Box sx={{ px: 2, py: 3, fontSize: 13, color: c.muted }}>Loading…</Box>
          ) : items.length === 0 ? (
            <Box sx={{ px: 2, py: 3, fontSize: 13, color: c.muted }}>Nothing here.</Box>
          ) : (
            items.map((n) => (
              <MenuItem
                key={n.eventId}
                onClick={() => void onItem(n)}
                sx={{
                  alignItems: "flex-start",
                  whiteSpace: "normal",
                  py: 1.25,
                  gap: 1,
                  opacity: n.doneAt ? 0.55 : 1,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    mt: "6px",
                    flex: "none",
                    bgcolor: n.doneAt ? "transparent" : c.teal,
                    border: n.doneAt ? `1px solid ${c.border}` : "none",
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ fontSize: 13, fontWeight: 600, color: c.ink2, lineHeight: 1.35 }}>
                    {n.title || `${n.source}/${n.sourceId}`}
                  </Box>
                  <Box sx={{ fontSize: 12, color: c.muted, mt: 0.25 }}>{reasonLabel(n)}</Box>
                  {typeof n.payload?.body === "string" && (
                    <Box sx={{ fontSize: 12.5, color: c.ink4, mt: 0.5, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {n.payload.body}
                    </Box>
                  )}
                  <Box sx={{ fontSize: 11, color: c.faint, fontFamily: brainTokens.font.mono, mt: 0.5 }}>
                    {relDate(n.ts)}
                  </Box>
                </Box>
              </MenuItem>
            ))
          )}
        </Box>
        <Box
          component="button"
          onClick={() => { setAnchor(null); navigate("/desk"); }}
          sx={{
            width: "100%", border: "none", borderTop: `1px solid ${c.borderLight}`,
            background: c.canvas, cursor: "pointer", py: 1.25, fontFamily: brainTokens.font.sans,
            fontSize: 12.5, fontWeight: 600, color: c.tealInk,
            "&:hover": { background: c.tealTint },
          }}
        >
          Open Desk →
        </Box>
      </Menu>
    </>
  );
}
